import React, { useState, useCallback, useRef } from 'react';
import { Upload, Palette, Download, Loader2, Sparkles, X, Image as ImageIcon } from 'lucide-react';
import API_URL from '../api/baseURL';

// Popular automotive paint colors
const POPULAR_COLORS = [
    { name: 'Racing Red', hex: '#B91C1C' },
    { name: 'Midnight Black', hex: '#1F2937' },
    { name: 'Pearl White', hex: '#F9FAFB' },
    { name: 'Metallic Blue', hex: '#1E40AF' },
    { name: 'Forest Green', hex: '#166534' },
    { name: 'Sunset Orange', hex: '#EA580C' },
    { name: 'Royal Purple', hex: '#7C3AED' },
    { name: 'Silver Metallic', hex: '#9CA3AF' },
    { name: 'Ocean Teal', hex: '#0D9488' },
    { name: 'Golden Bronze', hex: '#B45309' },
    { name: 'Navy Blue', hex: '#1E3A8A' },
    { name: 'Cherry Red', hex: '#DC2626' },
];

export default function CarPaintSimulator() {
    const [uploadedImage, setUploadedImage] = useState(null);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [selectedColor, setSelectedColor] = useState('#1E40AF');
    const [colorName, setColorName] = useState('Metallic Blue');
    const [generatedImage, setGeneratedImage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    // Handle file selection
    const handleFile = useCallback((file) => {
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Please upload an image file (JPG, PNG, etc.)');
            return;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            setError('Image too large. Maximum size is 10MB.');
            return;
        }

        setError('');
        setUploadedFile(file);
        setGeneratedImage(null);

        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => setUploadedImage(e.target.result);
        reader.readAsDataURL(file);
    }, []);

    // Drag and drop handlers
    const handleDrag = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFile(e.dataTransfer.files[0]);
        }
    }, [handleFile]);

    const handleFileInput = (e) => {
        if (e.target.files && e.target.files[0]) {
            handleFile(e.target.files[0]);
        }
    };

    // Color selection
    const handleColorSelect = (color, name) => {
        setSelectedColor(color);
        setColorName(name);
        setGeneratedImage(null);
    };

    const handleCustomColor = (e) => {
        setSelectedColor(e.target.value);
        setColorName('Custom Color');
        setGeneratedImage(null);
    };

    // Generate simulation
    const handleGenerate = async () => {
        if (!uploadedFile) {
            setError('Please upload a car photo first.');
            return;
        }

        setLoading(true);
        setError('');
        setGeneratedImage(null);

        try {
            const formData = new FormData();
            formData.append('image', uploadedFile);
            formData.append('color', selectedColor);
            formData.append('colorName', colorName);

            const response = await fetch(`${API_URL}/color-simulator/generate`, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || data.message || 'Generation failed');
            }

            setGeneratedImage(data.resultUrl);
        } catch (err) {
            setError(err.message || 'Failed to generate simulation. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Download result
    const handleDownload = () => {
        if (!generatedImage) return;

        const link = document.createElement('a');
        link.href = generatedImage;
        link.download = `car-paint-simulation-${colorName.replace(/\s+/g, '-').toLowerCase()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Clear/reset
    const handleClear = () => {
        setUploadedImage(null);
        setUploadedFile(null);
        setGeneratedImage(null);
        setError('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    return (
        <section id="color-simulator" className="py-24 bg-gradient-to-b from-[#0d1017] to-[#0a0c12] relative overflow-hidden">
            {/* Background decorations */}
            <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-purple-600/10 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-blue-600/10 rounded-full blur-[100px] pointer-events-none" />

            <div className="container mx-auto px-6 relative z-10">
                {/* Header */}
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 text-purple-400 font-bold text-xs uppercase tracking-wider mb-6">
                        <Sparkles className="w-4 h-4" />
                        Nouveau Outil
                    </div>
                    <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
                        SIMULATEUR DE <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">PEINTURE</span>
                    </h2>
                    <p className="text-xl text-slate-300">
                        Visualisez votre voiture dans une nouvelle couleur avant de peindre.
                        <br />
                        <span className="text-white font-semibold">Téléchargez une photo, choisissez une couleur, et voyez le résultat !</span>
                    </p>
                </div>

                <div className="max-w-5xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-8">

                        {/* Left Column: Upload & Color Selection */}
                        <div className="space-y-6">

                            {/* Upload Area */}
                            <div className="bg-[#151923] rounded-2xl border border-white/10 p-6">
                                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                                    <Upload className="w-5 h-5 text-blue-400" />
                                    1. Téléchargez une photo de votre voiture
                                </h3>

                                <div
                                    onDragEnter={handleDrag}
                                    onDragLeave={handleDrag}
                                    onDragOver={handleDrag}
                                    onDrop={handleDrop}
                                    onClick={() => fileInputRef.current?.click()}
                                    className={`
                    relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
                    ${dragActive
                                            ? 'border-blue-500 bg-blue-500/10'
                                            : 'border-white/20 hover:border-white/40 hover:bg-white/5'
                                        }
                    ${uploadedImage ? 'border-green-500/50' : ''}
                  `}
                                >
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileInput}
                                        className="hidden"
                                    />

                                    {uploadedImage ? (
                                        <div className="relative">
                                            <img
                                                src={uploadedImage}
                                                alt="Uploaded car"
                                                className="max-h-48 mx-auto rounded-lg object-contain"
                                            />
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleClear(); }}
                                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <ImageIcon className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                                            <p className="text-slate-400 mb-2">
                                                Glissez-déposez une image ici
                                            </p>
                                            <p className="text-slate-500 text-sm">
                                                ou cliquez pour sélectionner
                                            </p>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Color Selection */}
                            <div className="bg-[#151923] rounded-2xl border border-white/10 p-6">
                                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                                    <Palette className="w-5 h-5 text-purple-400" />
                                    2. Choisissez une couleur
                                </h3>

                                {/* Custom color picker */}
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="relative">
                                        <input
                                            type="color"
                                            value={selectedColor}
                                            onChange={handleCustomColor}
                                            className="w-14 h-14 rounded-xl cursor-pointer border-2 border-white/20"
                                        />
                                    </div>
                                    <div>
                                        <p className="text-white font-semibold">{colorName}</p>
                                        <p className="text-slate-400 text-sm">{selectedColor.toUpperCase()}</p>
                                    </div>
                                </div>

                                {/* Popular colors grid */}
                                <p className="text-slate-400 text-sm mb-3">Couleurs populaires :</p>
                                <div className="grid grid-cols-6 gap-2">
                                    {POPULAR_COLORS.map((color) => (
                                        <button
                                            key={color.hex}
                                            onClick={() => handleColorSelect(color.hex, color.name)}
                                            title={color.name}
                                            className={`
                        w-full aspect-square rounded-lg transition-all border-2
                        ${selectedColor === color.hex
                                                    ? 'border-white scale-110 shadow-lg'
                                                    : 'border-transparent hover:border-white/50 hover:scale-105'
                                                }
                      `}
                                            style={{ backgroundColor: color.hex }}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Generate Button */}
                            <button
                                onClick={handleGenerate}
                                disabled={!uploadedImage || loading}
                                className={`
                  w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 transition-all
                  ${!uploadedImage || loading
                                        ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                                        : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-500 hover:to-pink-500 shadow-lg shadow-purple-900/30'
                                    }
                `}
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        Génération en cours...
                                    </>
                                ) : (
                                    <>
                                        <Sparkles className="w-5 h-5" />
                                        Simuler la nouvelle couleur
                                    </>
                                )}
                            </button>

                            {error && (
                                <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl p-4 text-sm">
                                    {error}
                                </div>
                            )}
                        </div>

                        {/* Right Column: Result Display */}
                        <div className="bg-[#151923] rounded-2xl border border-white/10 p-6 flex flex-col">
                            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                                <ImageIcon className="w-5 h-5 text-green-400" />
                                Résultat
                            </h3>

                            <div className="flex-1 flex items-center justify-center min-h-[300px] bg-[#0a0c12] rounded-xl border border-white/5">
                                {loading ? (
                                    <div className="text-center">
                                        <Loader2 className="w-12 h-12 text-purple-500 animate-spin mx-auto mb-4" />
                                        <p className="text-slate-400">L'IA génère votre aperçu...</p>
                                        <p className="text-slate-500 text-sm mt-2">Cela peut prendre 10-30 secondes</p>
                                    </div>
                                ) : generatedImage ? (
                                    <img
                                        src={generatedImage}
                                        alt="Generated result"
                                        className="max-w-full max-h-80 rounded-lg object-contain"
                                    />
                                ) : (
                                    <div className="text-center text-slate-500">
                                        <ImageIcon className="w-16 h-16 mx-auto mb-4 opacity-30" />
                                        <p>L'aperçu apparaîtra ici</p>
                                    </div>
                                )}
                            </div>

                            {generatedImage && (
                                <button
                                    onClick={handleDownload}
                                    className="mt-4 w-full py-3 bg-green-600 hover:bg-green-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-colors"
                                >
                                    <Download className="w-5 h-5" />
                                    Télécharger l'image
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Info note */}
                    <div className="mt-8 bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 text-center">
                        <p className="text-blue-300 text-sm">
                            <strong>Note :</strong> La qualité du résultat dépend de la photo originale.
                            Pour de meilleurs résultats, utilisez une photo claire avec le véhicule bien visible.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}
